import React, { useState, useEffect } from 'react';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import EditableSection from 'components/v2/EditableSection';
import Label from 'components/v2/forms/Label';
import Select from 'components/v2/select';
import TextField from 'components/v2/forms/TextField';
import { useTranslation } from 'react-i18next';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import { titleize } from 'underscore.string';
import { formatMoney } from 'accounting';
import { getCurrencySymbol } from 'components/modules/master/formatters';
import { checkPermissonForEditShipperBillingModule, payloadMappingOnlyForChanges, } from './utils';
const MAX_CREDIT_LIMIT = 999999999.99;
const PaymentTermsSummary = ({ fields, overwritePaymentTermsSlugs }) => {
    const { t } = useTranslation();
    const drayageSlug = overwritePaymentTermsSlugs
        ? 'FIRST_MILE_MID_MILE'
        : 'DRAYAGE';
    const shorthaulSlug = overwritePaymentTermsSlugs ? 'LAST_MILE' : 'SHORTHAUL';
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "grid grid-cols-2 gap-4" },
            React.createElement(DescriptionListItem, { label: t('CREDIT_LIMIT'), className: "" }, fields.credit_limit
                ? formatMoney(fields.credit_limit, getCurrencySymbol(DEFAULT_CURRENCY))
                : t('NOT_SET'))),
        React.createElement("hr", null),
        React.createElement("p", { className: "text-sm text-gray-700 font-medium mb-2" }, t(drayageSlug)),
        React.createElement("div", { className: "grid grid-cols-2 gap-4" },
            React.createElement(DescriptionListItem, { label: t('PAYMENT_METHOD'), className: "" }, fields.drayage_payment_method
                ? titleize(fields.drayage_payment_method)
                : t('NOT_SET')),
            React.createElement(DescriptionListItem, { label: t('PAYMENT_TERMS'), className: "" }, fields.drayage_terms ? fields.drayage_terms : t('NOT_SET'))),
        React.createElement("hr", { className: "my-4" }),
        React.createElement("p", { className: "text-sm text-gray-700 font-medium mb-2" }, t(shorthaulSlug)),
        React.createElement("div", { className: "grid grid-cols-2 gap-4" },
            React.createElement(DescriptionListItem, { label: t('PAYMENT_METHOD'), className: "" }, fields.shorthaul_payment_method
                ? titleize(fields.shorthaul_payment_method)
                : t('NOT_SET')),
            React.createElement(DescriptionListItem, { label: t('PAYMENT_TERMS'), className: "" }, fields.shorthaul_terms ? fields.shorthaul_terms : t('NOT_SET')))));
};
const PaymentTermsForm = ({ onChange, onSaveShipper, onSaveSuccess, onSaveFail, saving, fields = {}, }) => {
    var _a, _b, _c, _d, _e, _f;
    const { t } = useTranslation();
    const setErrors = (field, value) => {
        onChange('error', Object.assign(Object.assign({}, fields.error), { [field]: value }));
    };
    const paymentMethodOptions = [
        {
            value: 'manual',
            name: 'Manual',
            localizationSlug: 'MANUAL',
        },
        {
            value: 'automated',
            name: 'Automated',
            localizationSlug: 'AUTOMATED',
        },
    ];
    const getPaymentOptions = (flag) => {
        if (flag) {
            return paymentMethodOptions;
        }
        return paymentMethodOptions.filter((o) => o.value !== 'automated');
    };
    useEffect(() => {
        var _a;
        if (fields.drayage_terms > 120 ||
            fields.drayage_terms < 0 ||
            !fields.drayage_terms) {
            setErrors('drayage_terms', t('SHOULD_BE_BETWEEN_NUM1_NUM2', { num1: 0, num2: 120 }));
        }
        else if ((_a = fields === null || fields === void 0 ? void 0 : fields.error) === null || _a === void 0 ? void 0 : _a.drayage_terms) {
            setErrors('drayage_terms', '');
        }
    }, [fields.drayage_terms]);
    const saveForm = async () => {
        var _a, _b, _c;
        let isValid = false;
        if ((!((_a = fields === null || fields === void 0 ? void 0 : fields.error) === null || _a === void 0 ? void 0 : _a.drayage_terms) && !((_b = fields === null || fields === void 0 ? void 0 : fields.error) === null || _b === void 0 ? void 0 : _b.shorthaul_terms)) ||
            !((_c = fields === null || fields === void 0 ? void 0 : fields.error) === null || _c === void 0 ? void 0 : _c.credit_limit)) {
            isValid = true;
        }
        try {
            if (isValid) {
                await onSaveShipper();
                onSaveSuccess(fields);
            }
        }
        catch (e) {
            onSaveFail();
        }
    };
    useEffect(() => {
        var _a;
        if (fields.shorthaul_terms > 120 ||
            fields.shorthaul_terms < 0 ||
            !fields.shorthaul_terms) {
            setErrors('shorthaul_terms', t('SHOULD_BE_BETWEEN_NUM1_NUM2', { num1: 0, num2: 120 }));
        }
        else if ((_a = fields === null || fields === void 0 ? void 0 : fields.error) === null || _a === void 0 ? void 0 : _a.shorthaul_terms) {
            setErrors('shorthaul_terms', '');
        }
    }, [fields.shorthaul_terms]);
    useEffect(() => {
        if (saving) {
            saveForm();
        }
    }, [saving]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "grid grid-cols-2 gap-4" },
            React.createElement("div", null,
                React.createElement(Label, null, t('CREDIT_LIMIT')),
                React.createElement(TextField, { className: "mt-1", type: "number", min: 0, max: MAX_CREDIT_LIMIT, value: fields.credit_limit, onChange: (val) => onChange('credit_limit', val) }),
                ((_a = fields === null || fields === void 0 ? void 0 : fields.error) === null || _a === void 0 ? void 0 : _a.credit_limit) && (React.createElement(ErrorLabel, { className: "mt-1" }, (_b = fields === null || fields === void 0 ? void 0 : fields.error) === null || _b === void 0 ? void 0 : _b.credit_limit)))),
        React.createElement("hr", null),
        React.createElement("p", { className: "text-sm text-gray-700 font-medium mb-2" }, t('DRAYAGE')),
        React.createElement("div", { className: "grid grid-cols-2 gap-4" },
            React.createElement("div", null,
                React.createElement(Label, null, t('PAYMENT_METHOD')),
                React.createElement(Select, { labelClassName: "m-0", items: getPaymentOptions(fields.hasCreditCardOnFile), valueKey: "value", textKey: "localizationSlug", selected: fields.drayage_payment_method, onSelect: ({ name }) => onChange('drayage_payment_method', name) })),
            React.createElement("div", null,
                React.createElement(Label, null, t('PAYMENT_TERMS')),
                React.createElement(TextField, { className: "mt-1", type: "number", value: fields.drayage_terms, onChange: (val) => onChange('drayage_terms', val) }),
                ((_c = fields === null || fields === void 0 ? void 0 : fields.error) === null || _c === void 0 ? void 0 : _c.drayage_terms) && (React.createElement(ErrorLabel, { className: "mt-1" }, (_d = fields === null || fields === void 0 ? void 0 : fields.error) === null || _d === void 0 ? void 0 : _d.drayage_terms)))),
        React.createElement("hr", { className: "my-4" }),
        React.createElement("p", { className: "text-sm text-gray-700 font-medium mb-2" }, t('SHORTHAUL')),
        React.createElement("div", { className: "grid grid-cols-2 gap-4" },
            React.createElement("div", null,
                React.createElement(Label, null, t('PAYMENT_METHOD')),
                React.createElement(Select, { labelClassName: "m-0", items: getPaymentOptions(fields.hasCreditCardOnFile), valueKey: "value", textKey: "localizationSlug", selected: fields.shorthaul_payment_method, onSelect: ({ name }) => {
                        onChange('shorthaul_payment_method', name);
                    } })),
            React.createElement("div", null,
                React.createElement(Label, null, t('PAYMENT_TERMS')),
                React.createElement(TextField, { className: "mt-1", type: "number", value: fields.shorthaul_terms, onChange: (val) => onChange('shorthaul_terms', val) }),
                ((_e = fields === null || fields === void 0 ? void 0 : fields.error) === null || _e === void 0 ? void 0 : _e.shorthaul_terms) && (React.createElement(ErrorLabel, { className: "mt-1" }, (_f = fields === null || fields === void 0 ? void 0 : fields.error) === null || _f === void 0 ? void 0 : _f.shorthaul_terms))))));
};
const PaymentTerms = ({ onUpdateShipper, className, data, overwritePaymentTermsSlugs = false, }) => {
    const { t } = useTranslation();
    const [initialData, setInitialData] = useState(data);
    const [paymentTermsData, setPaymentTermsData] = useState(Object.assign(Object.assign({}, data), { error: {
            drayage_terms: '',
            shorthaul_terms: '',
        } }));
    const onChange = (field, value) => {
        let actualValue = value;
        if ((field === 'drayage_payment_method' ||
            field === 'shorthaul_payment_method') &&
            value) {
            actualValue = value.toLowerCase();
        }
        if (field === 'credit_limit') {
            if (value.includes('-')) {
                actualValue = Math.abs(value);
            }
            if (value > MAX_CREDIT_LIMIT) {
                actualValue = MAX_CREDIT_LIMIT;
            }
        }
        setPaymentTermsData((prev) => (Object.assign(Object.assign({}, prev), { [field]: actualValue })));
    };
    const getInitData = () => {
        if (data) {
            setPaymentTermsData((prev) => {
                return Object.assign(Object.assign({}, prev), data);
            });
            setInitialData(data);
        }
    };
    const handleSave = async () => {
        const payload = payloadMappingOnlyForChanges(initialData, paymentTermsData);
        if (Object.keys(payload).length) {
            await onUpdateShipper(payload);
        }
    };
    const handleAfterSave = (onSaveSuccess) => {
        return (updatedData = {}) => {
            setInitialData((prev) => (Object.assign(Object.assign({}, prev), updatedData)));
            onSaveSuccess();
        };
    };
    useEffect(() => {
        getInitData();
    }, [data]);
    return (React.createElement(EditableSection, { title: t('CREDIT_LIMIT_AND_PAYMENT_TERMS'), canEdit: checkPermissonForEditShipperBillingModule(), className: className }, (editMode, saving, onSaveSuccess, onSaveFail) => editMode ? (React.createElement(PaymentTermsForm, { onChange: onChange, fields: paymentTermsData, saving: saving, onSaveSuccess: handleAfterSave(onSaveSuccess), onSaveFail: () => onSaveFail(), onSaveShipper: () => handleSave() })) : (React.createElement(PaymentTermsSummary, { fields: initialData, overwritePaymentTermsSlugs: overwritePaymentTermsSlugs }))));
};
export default PaymentTerms;
